/**
 * The brand-study poller, lifted out of the wizard shell.
 *
 * Everything the reading screen needs and nothing else: start a read, watch it,
 * and give up honestly when it is plainly not coming back. It owns the two
 * ceilings that keep the screen from being held forever — a wall-clock deadline
 * and a run of unreachable polls — and dispatches the record into the wizard's
 * reducer itself, so the shell only renders what comes back.
 */
import { useCallback, useEffect, useRef, useState, type Dispatch } from "react";
import {
  BrandStudyBusyError,
  getBrandStudy,
  startBrandStudy,
  type BrandStudyRecord,
  type StudySource,
} from "../../../api";
import type { BrandSetupAction, BrandSetupSource } from "../../../lib/brandSetup/state";

const POLL_MS = 1500;
/** A read that has neither finished nor failed by here is not coming back.
 *  Without a ceiling the reading screen owns the wizard forever. */
const STUDY_DEADLINE_MS = 6 * 60 * 1000;
/** …and the same for a record we simply cannot reach (404 after a prune, a
 *  proxy in the way, a redeploy between the POST and the first GET). */
const MAX_POLL_ERRORS = 20;
const STUDY_TIMEOUT_SENTENCE =
  "This is taking too long. You can set things up by hand and read your brand again later.";

/** UI source → the shape `POST /api/brand-study/start` takes. */
function toServerSource(s: BrandSetupSource): StudySource {
  return s.kind === "url" ? { kind: "url", url: s.url } : { kind: "upload", uploadId: s.uploadId, filename: s.filename };
}

export interface BrandStudyController {
  record: BrandStudyRecord | null;
  studying: boolean;
  error: string | null;
  /** Start a read of the current sources (clears the previous verdict). */
  start: () => Promise<void>;
  /** Stop watching and hand the screen back — "go back to my sources". */
  stop: () => void;
  clearError: () => void;
}

export function useBrandStudy(
  sources: BrandSetupSource[],
  dispatch: Dispatch<BrandSetupAction>,
): BrandStudyController {
  const [record, setRecord] = useState<BrandStudyRecord | null>(null);
  const [studying, setStudying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);
  const pollErrorsRef = useRef(0);
  const deadlineRef = useRef(0);

  const stopPolling = useCallback((): void => {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);
  useEffect(() => stopPolling, [stopPolling]);

  const giveUp = useCallback((): void => {
    stopPolling();
    setStudying(false);
    setError(STUDY_TIMEOUT_SENTENCE);
  }, [stopPolling]);

  const poll = useCallback(
    (id: string): void => {
      stopPolling();
      pollErrorsRef.current = 0;
      deadlineRef.current = Date.now() + STUDY_DEADLINE_MS;
      const tick = async (): Promise<void> => {
        let rec: BrandStudyRecord;
        try {
          rec = await getBrandStudy(id);
        } catch {
          // A blip; the next tick tries again — but not forever.
          pollErrorsRef.current += 1;
          if (pollErrorsRef.current >= MAX_POLL_ERRORS) giveUp();
          return;
        }
        pollErrorsRef.current = 0;
        setRecord(rec);
        if (rec.status !== "done" && rec.status !== "failed") {
          if (Date.now() > deadlineRef.current) giveUp();
          return;
        }
        stopPolling();
        setStudying(false);
        // The reading screen STAYS up either way: it is where the three ways
        // on live (and, when nothing landed, the failure sentences).
        if (rec.status === "done" && (rec.chips ?? []).length > 0) dispatch({ type: "study", record: rec });
      };
      void tick();
      pollRef.current = window.setInterval(() => void tick(), POLL_MS);
    },
    [dispatch, giveUp, stopPolling],
  );

  const start = useCallback(async (): Promise<void> => {
    setError(null);
    setRecord(null);
    setStudying(true);
    try {
      const { id } = await startBrandStudy(sources.map(toServerSource));
      dispatch({ type: "patch", patch: { studyId: id } });
      poll(id);
    } catch (e) {
      setStudying(false);
      setError(
        e instanceof BrandStudyBusyError
          ? "Another brand is being read right now — try again in a minute."
          : e instanceof Error
            ? e.message
            : String(e),
      );
    }
  }, [dispatch, poll, sources]);

  const stop = useCallback((): void => {
    stopPolling();
    setStudying(false);
  }, [stopPolling]);

  return { record, studying, error, start, stop, clearError: () => setError(null) };
}
